import { useEffect } from 'react';

export default function SEOHead({
  title,
  description,
  canonicalUrl,
  jsonLd,
  ogImage,
  ogType = 'website',
  article, // { publishedTime, author, tags }
}) {
  useEffect(() => {
    document.title = title;

    const setMeta = (selector, attrName, attrValue, content) => {
      let tag = document.querySelector(selector);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attrName, attrValue);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    // description
    setMeta('meta[name="description"]', 'name', 'description', description || '');

    // canonical
    if (canonicalUrl) {
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.rel = 'canonical';
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.href = canonicalUrl;
    }

    // Open Graph
    const ogTags = [
      ['og:title', title],
      ['og:description', description || ''],
      ['og:type', ogType],
      ['og:url', canonicalUrl || (typeof window !== 'undefined' ? window.location.href : '')],
      ['og:site_name', 'Ogaba Joseph Attah'],
    ];
    if (ogImage) {
      ogTags.push(['og:image', ogImage]);
      ogTags.push(['og:image:secure_url', ogImage]);
      ogTags.push(['og:image:width', '1600']);
      ogTags.push(['og:image:height', '900']);
      ogTags.push(['og:image:alt', title]);
    }
    if (ogType === 'article' && article) {
      if (article.publishedTime) ogTags.push(['article:published_time', article.publishedTime]);
      if (article.author) ogTags.push(['article:author', article.author]);
      if (Array.isArray(article.tags)) {
        article.tags.forEach((t) => ogTags.push(['article:tag', t]));
      }
    }

    // Remove any existing article:tag tags first (they may multiply)
    document.querySelectorAll('meta[property="article:tag"]').forEach((n) => n.remove());

    ogTags.forEach(([property, content]) => {
      if (property === 'article:tag') {
        const tag = document.createElement('meta');
        tag.setAttribute('property', 'article:tag');
        tag.setAttribute('content', content);
        document.head.appendChild(tag);
      } else {
        setMeta(`meta[property="${property}"]`, 'property', property, content);
      }
    });

    // Twitter Card
    const twitterTags = [
      ['twitter:card', ogImage ? 'summary_large_image' : 'summary'],
      ['twitter:title', title],
      ['twitter:description', description || ''],
    ];
    if (ogImage) {
      twitterTags.push(['twitter:image', ogImage]);
      twitterTags.push(['twitter:image:alt', title]);
    }
    twitterTags.forEach(([name, content]) => {
      setMeta(`meta[name="${name}"]`, 'name', name, content);
    });

    // JSON-LD structured data
    if (jsonLd) {
      let script = document.querySelector('script[type="application/ld+json"]');
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    }
  }, [title, description, canonicalUrl, jsonLd, ogImage, ogType, article]);

  return null;
}
